// Employee Attendance — per-employee monthly punch-time table.
//
// One row per day of the month: Login / Lunch In / Lunch Out / Logout time
// punches, edited in a local "pending edits" buffer, then bulk-saved via
// markAttendanceAction. A day marked Leave/Absent (via the row's status menu)
// collapses its 4 time cells into a single merged label instead — hours
// worked are derived server-side from the punches, not entered directly.
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ArrowLeft, MoreVertical } from "lucide-react";
// import { X } from "lucide-react"; // was used by the removed "Clear" dropdown option
import moment from "moment";
import { cn } from "lib/utils";
import {
  getEmployeeAttendanceCalendarAction,
  getEmployeeAttendanceSummaryAction,
  markAttendanceAction,
} from "../../Store/actions/apparelAction";
import AppConstant from "../../Globals/AppConstants";
import { LoadingOverlay } from "../../shared/loading-overlay";
import { Button } from "ui/button";
import { Input } from "ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "ui/dropdown-menu";

// Fiscal-year month order (April -> March), each paired with its actual
// calendar month number.
const FISCAL_MONTHS = [
  { name: "April", value: 4 },
  { name: "May", value: 5 },
  { name: "June", value: 6 },
  { name: "July", value: 7 },
  { name: "August", value: 8 },
  { name: "September", value: 9 },
  { name: "October", value: 10 },
  { name: "November", value: 11 },
  { name: "December", value: 12 },
  { name: "January", value: 1 },
  { name: "February", value: 2 },
  { name: "March", value: 3 },
];

// Accounting year runs April - March; the filter only ever needs to cover
// the *current* accounting year, shown as a single "2026-2027"-style option
// rather than its two constituent calendar years.
const getAccountingYearBounds = () => {
  const now = new Date();
  const calendarMonth = now.getMonth() + 1; // 1-based
  const calendarYear = now.getFullYear();
  const startYear = calendarMonth >= 4 ? calendarYear : calendarYear - 1;
  return { startYear, endYear: startYear + 1 };
};

// Jan/Feb/Mar fall in the second calendar year of the accounting year;
// Apr-Dec fall in the first.
const calendarYearForMonth = (fiscalStartYear: number, month: number) =>
  month >= 4 ? fiscalStartYear : fiscalStartYear + 1;

// Statuses that replace the 4 punch-time cells with a single merged label —
// matched by name (case insensitive) against AppConstant.attendanceStatusOptions.
const NO_PUNCH_STATUSES = ["absent", "leave"];

// Row background per status, using the app's semantic status tokens (same
// ones StatusBadge/badge.tsx use) rather than raw Tailwind colors.
const ROW_STATUS_CLASSES: Record<string, string> = {
  present: "bg-success/10 hover:bg-success/15",
  leave: "bg-destructive/10 hover:bg-destructive/15",
  "half day": "bg-warning/15 hover:bg-warning/20",
};

type DayEdit = {
  statusRefId: number | null;
  loginTime: string;
  lunchInTime: string;
  lunchOutTime: string;
  logoutTime: string;
  remarks: string;
};

const emptyDay = (): DayEdit => ({
  statusRefId: null,
  loginTime: "",
  lunchInTime: "",
  lunchOutTime: "",
  logoutTime: "",
  remarks: "",
});

const EmployeeAttendanceCalendar = () => {
  const { employeeId } = useParams();
  const location: any = useLocation();
  const employeeName = location?.state?.employeeName;
  // Arriving from Audit Log: land on the record's own month and highlight
  // its day once the calendar loads (see the highlightedDay effect below).
  // Parsed from the "YYYY-MM-DD" components directly, not via `new Date`,
  // to avoid a UTC round trip shifting the day (same reasoning as the
  // backend's toLocalDateStr).
  const highlightDate: string | undefined = location?.state?.highlightDate;
  const highlightDateParts = highlightDate?.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const [highlightedDay, setHighlightedDay] = useState<number | undefined>(
    highlightDateParts ? Number(highlightDateParts[3]) : undefined
  );
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // The Attendance list carries its own currently-selected month/year across
  // via onMarkAttendance's navigate state, so this screen opens on the same
  // period the user was looking at instead of always defaulting to today's
  // calendar month — that mismatch was the reported bug (list showed August,
  // Mark Attendance opened on September). Audit Log's highlightDate still
  // wins when present, since it must land on that record's own month.
  const stateMonth: number | undefined = location?.state?.month;
  const stateFiscalStartYear: number | undefined = location?.state?.fiscalStartYear;

  const now = new Date();
  const [month, setMonth] = useState<number>(
    highlightDateParts ? Number(highlightDateParts[2]) : stateMonth ?? now.getMonth() + 1
  );
  const [fiscalStartYear] = useState<number>(stateFiscalStartYear ?? getAccountingYearBounds().startYear);
  const year = calendarYearForMonth(fiscalStartYear, month);
  const [dayEdits, setDayEditsState] = useState<Record<string, DayEdit>>({});
  // Mirrors dayEdits, updated synchronously alongside setDayEditsState (not
  // via a separate effect, which would lag a render behind) — lets
  // getDay()/saveKeyNow() read the true latest value even from a stale
  // closure (e.g. the unmount-flush cleanup below, captured once at mount).
  const dayEditsRef = useRef<Record<string, DayEdit>>({});
  const setDayEdits = (updater: Record<string, DayEdit> | ((prev: Record<string, DayEdit>) => Record<string, DayEdit>)) => {
    setDayEditsState((prev) => {
      const next = typeof updater === "function" ? (updater as any)(prev) : updater;
      dayEditsRef.current = next;
      return next;
    });
  };
  // Tracks the last value actually persisted per day, so auto-save (on blur /
  // status pick) only fires the API when something really changed — e.g.
  // tabbing through an untouched field, or a picker re-firing the same value,
  // shouldn't trigger a network call.
  const lastSavedRef = useRef<Record<string, DayEdit>>({});
  // Identifies the current "view" (employee+month+year) so the seeding effect
  // can tell a genuine navigation (safe to fully reset dayEdits) apart from a
  // same-view background refetch (must merge, not clobber in-progress edits).
  const viewContextRef = useRef<string>("");
  // Per-day debounce timers for auto-save — see scheduleAutoSave below.
  const saveTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const apparelReducerState: any = useSelector((state: any) => state.ApparelReducerState);
  const {
    getEmployeeAttendanceCalendarData,
    getEmployeeAttendanceCalendarLoad,
    getEmployeeAttendanceSummaryData,
    markAttendanceLoad,
  } = apparelReducerState;

  const summary = getEmployeeAttendanceSummaryData?.getEmployeeAttendanceSummary;

  // Memoized so the fallback `[]` doesn't mint a new array reference on every
  // render — an unstable reference here would retrigger the seeding effect
  // below on every render (infinite "Maximum update depth exceeded" loop),
  // wiping out any in-progress edits almost as soon as they're typed.
  const calendarDays: any[] = useMemo(
    () => getEmployeeAttendanceCalendarData?.getEmployeeAttendanceCalendar || [],
    [getEmployeeAttendanceCalendarData]
  );
  const statusOptions: any[] = AppConstant.attendanceStatusOptions;
  const highlightedRowRef = useRef<HTMLTableRowElement | null>(null);

  // Scroll the Audit Log's target day into view once its month's data has
  // loaded; the highlight itself fades after 5s so it doesn't linger
  // indefinitely if the user keeps working on this page.
  useEffect(() => {
    if (highlightedDay == null || calendarDays.length === 0) return;
    highlightedRowRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    const t = setTimeout(() => setHighlightedDay(undefined), 5000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarDays]);

  const getCalendarApi = () => {
    dispatch(getEmployeeAttendanceCalendarAction({ employeeId, month, year }));
  };

  useEffect(() => {
    getCalendarApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, month, year]);

  useEffect(() => {
    dispatch(getEmployeeAttendanceSummaryAction({ employeeId, month, year }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, month, year, markAttendanceLoad]);

  // Seed the local edit buffer from whatever the backend already has for the
  // month. `calendarDays` also changes on a same-view background refetch
  // (e.g. right after the bulk Save button triggers getCalendarApi()) — if
  // that happens while the user is mid-edit on some *other* day, a blind
  // overwrite would silently wipe their unsaved keystrokes. So: a genuine
  // employee/month/year switch fully resets (today's behavior), but a
  // same-view refetch only refreshes days that have no unsaved local edits
  // (i.e. are still equal to their last-saved baseline).
  useEffect(() => {
    const serverByKey: Record<string, DayEdit> = {};
    (calendarDays || []).forEach((d: any) => {
      const key = moment.utc(d?.attendanceDate).format("YYYY-MM-DD");
      serverByKey[key] = {
        statusRefId: d?.statusRefId ?? null,
        loginTime: d?.loginTime || "",
        lunchInTime: d?.lunchInTime || "",
        lunchOutTime: d?.lunchOutTime || "",
        logoutTime: d?.logoutTime || "",
        remarks: d?.remarks ?? "",
      };
    });

    const viewContext = `${employeeId}-${month}-${year}`;
    const isNewView = viewContextRef.current !== viewContext;
    viewContextRef.current = viewContext;

    if (isNewView) {
      setDayEdits(serverByKey);
      lastSavedRef.current = Object.fromEntries(
        Object.entries(serverByKey).map(([key, value]) => [key, { ...value }])
      );
      return;
    }

    setDayEdits((prev) => {
      const next = { ...prev };
      
      Object.keys(next).forEach((key) => {
        const localEdit = next[key];
        const isDirty = localEdit && !daysEqual(localEdit, lastSavedRef.current[key] || emptyDay());
        if (!isDirty && !serverByKey[key]) {
          delete next[key];
        }
      });

      Object.entries(serverByKey).forEach(([key, serverEdit]) => {
        const localEdit = prev[key];
        const isDirty = localEdit && !daysEqual(localEdit, lastSavedRef.current[key] || emptyDay());
        if (!isDirty) {
          next[key] = serverEdit;
        }
        lastSavedRef.current[key] = serverEdit;
      });

      Object.keys(lastSavedRef.current).forEach((key) => {
        if (!next[key] && !serverByKey[key]) {
          delete lastSavedRef.current[key];
        }
      });

      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarDays]);

  const daysInMonth = useMemo(() => moment(`${year}-${month}`, "YYYY-M").daysInMonth(), [year, month]);

  const dateKeyForDay = (day: number) => moment(`${year}-${month}-${day}`, "YYYY-M-D").format("YYYY-MM-DD");

  const statusName = (statusRefId: number | null | undefined) => {
    const match = statusOptions?.find((s: any) => s?.id === statusRefId);
    return String(match?.name || "").toLowerCase();
  };

  const isNoPunchStatus = (statusRefId: number | null | undefined) =>
    NO_PUNCH_STATUSES.includes(statusName(statusRefId));

  // "HH:mm" -> minutes since midnight, or null if not a valid punch time.
  const toMinutes = (time: string | null | undefined): number | null => {
    if (!time || !/^\d{1,2}:\d{2}$/.test(time)) {
      return null;
    }
    const [hours, minutes] = time.split(":").map(Number);
    return hours * 60 + minutes;
  };

  // Mirrors AttendanceService.computeWorkingHours on the backend — Logout
  // minus Login, minus the lunch break if both lunch punches are set.
  const computeTotalHours = (edit: DayEdit): number | null => {
    const login = toMinutes(edit.loginTime);
    const logout = toMinutes(edit.logoutTime);
    if (login == null || logout == null || logout <= login) {
      return null;
    }
    const lunchIn = toMinutes(edit.lunchInTime);
    const lunchOut = toMinutes(edit.lunchOutTime);
    const lunchMinutes = lunchIn != null && lunchOut != null && lunchOut > lunchIn ? lunchOut - lunchIn : 0;
    return Math.round(((logout - login - lunchMinutes) / 60) * 100) / 100;
  };

  // Decimal hours (e.g. 18.13) -> "hh:mm" (e.g. "18:08") — decimal hours is a
  // fraction of 60 minutes, not minutes itself, so the digits after the
  // decimal point don't match minutes directly.
  const formatHoursAsHhMm = (decimalHours: number | null | undefined): string => {
    if (decimalHours == null || Number.isNaN(decimalHours)) {
      return "-";
    }
    const totalMinutes = Math.round(decimalHours * 60);
    const sign = totalMinutes < 0 ? "-" : "";
    const absMinutes = Math.abs(totalMinutes);
    const hh = Math.floor(absMinutes / 60);
    const mm = absMinutes % 60;
    return `${sign}${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
  };

  const presentStatusId = useMemo(
    () => statusOptions?.find((s: any) => String(s?.name || "").toLowerCase() === "present")?.id ?? null,
    [statusOptions]
  );

  const getDay = (key: string): DayEdit => dayEditsRef.current[key] || emptyDay();

  const daysEqual = (a: DayEdit, b: DayEdit) =>
    a.statusRefId === b.statusRefId &&
    a.loginTime === b.loginTime &&
    a.lunchInTime === b.lunchInTime &&
    a.lunchOutTime === b.lunchOutTime &&
    a.logoutTime === b.logoutTime;

  const setDayField = (day: number, field: keyof DayEdit, value: any) => {
    const key = dateKeyForDay(day);
    setDayEdits((prev) => ({
      ...prev,
      [key]: { ...emptyDay(), ...prev[key], [field]: value },
    }));
  };

  // Sets status and auto-saves in one shot, computing the merged day value
  // synchronously rather than reading it back from state — setDayEdits is
  // async, so a saveKeyNow(key) called right after would otherwise still see
  // the pre-update value. Cancels any pending debounced punch-time save for
  // this day first, so a fresher status change can't be raced/overwritten by
  // a stale debounced write firing afterward.
  const setDayStatusAndSave = (day: number, statusRefId: number | null) => {
    const key = dateKeyForDay(day);
    if (saveTimersRef.current[key]) {
      clearTimeout(saveTimersRef.current[key]);
      delete saveTimersRef.current[key];
    }

    const current = { ...emptyDay(), ...getDay(key) };
    const clearPunches = statusRefId != null && NO_PUNCH_STATUSES.includes(statusName(statusRefId));
    const edit: DayEdit = {
      ...current,
      statusRefId,
      ...(clearPunches ? { loginTime: "", lunchInTime: "", lunchOutTime: "", logoutTime: "" } : {}),
    };
    setDayEdits((prev) => ({ ...prev, [key]: edit }));

    if (daysEqual(edit, lastSavedRef.current[key] || emptyDay())) {
      return;
    }
    const payload = buildDayPayload(key, edit);
    if (payload) {
      lastSavedRef.current[key] = edit;
      dispatch(markAttendanceAction({ employeeId, days: [payload] }));
    }
  };

  // Builds the mutation-ready payload for one day, or null if there's nothing
  // meaningful to save yet (no status and no punch times).
  const buildDayPayload = (attendanceDate: string, edit: DayEdit) => {
    const hasSignal = edit.statusRefId || edit.loginTime || edit.lunchInTime || edit.lunchOutTime || edit.logoutTime;
    if (!hasSignal) {
      return null;
    }
    const statusRefId = edit.statusRefId ?? presentStatusId;
    if (statusRefId == null) {
      return null;
    }
    return {
      attendanceDate,
      statusRefId,
      loginTime: edit.loginTime || null,
      lunchInTime: edit.lunchInTime || null,
      lunchOutTime: edit.lunchOutTime || null,
      logoutTime: edit.logoutTime || null,
      remarks: edit.remarks || "",
    };
  };

  const saveKeyNow = (key: string) => {
    const edit = getDay(key);
    if (daysEqual(edit, lastSavedRef.current[key] || emptyDay())) {
      return;
    }
    const payload = buildDayPayload(key, edit);
    if (!payload) {
      return;
    }
    lastSavedRef.current[key] = edit;
    dispatch(markAttendanceAction({ employeeId, days: [payload] }));
  };

  // Debounced auto-save: there's no way to hook an "OK" button into the
  // native time-picker itself, so a field losing focus is the closest
  // equivalent — but Login/Lunch In/Lunch Out/Logout blur independently, and
  // saving immediately on every one of the 4 blurs would fire 4 separate
  // requests for one day. Debouncing per day-key coalesces a quick run
  // through all 4 fields into a single request once the user moves on.
  const scheduleAutoSave = (day: number) => {
    const key = dateKeyForDay(day);
    if (saveTimersRef.current[key]) {
      clearTimeout(saveTimersRef.current[key]);
    }
    saveTimersRef.current[key] = setTimeout(() => {
      delete saveTimersRef.current[key];
      saveKeyNow(key);
    }, 500);
  };

  // Flush (not discard) any pending debounced saves on unmount, so navigating
  // away right after typing doesn't lose the last edit.
  useEffect(() => {
    return () => {
      Object.entries(saveTimersRef.current).forEach(([key, timer]) => {
        clearTimeout(timer);
        saveKeyNow(key);
      });
      saveTimersRef.current = {};
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="mz h-full overflow-y-auto px-6 py-4">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/attendance")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl font-semibold text-foreground">
              {employeeName || `Employee #${employeeId}`}
            </h1>
            <p className="text-sm text-muted-foreground">Mark daily attendance</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v, 10))}>
            <SelectTrigger className="h-9 w-[140px]">
              <SelectValue placeholder="Month" />
            </SelectTrigger>
            <SelectContent>
              {FISCAL_MONTHS.map(({ name, value }) => (
                <SelectItem key={name} value={String(value)}>
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={String(fiscalStartYear)}>
            <SelectTrigger className="h-9 w-[110px]">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={String(fiscalStartYear)}>
                {fiscalStartYear}-{fiscalStartYear + 1}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mb-4 grid grid-cols-2 gap-3 rounded-xl border bg-muted/30 p-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7">
        <div>
          <p className="text-xs uppercase text-muted-foreground">Name</p>
          <p className="text-sm font-medium text-foreground">
            {summary?.employeeName || employeeName || `Employee #${employeeId}`}
          </p>
        </div>
        <div>
          <p className="text-xs uppercase text-muted-foreground">Work Type</p>
          <p className="text-sm font-medium text-foreground">{summary?.workType ?? "-"}</p>
        </div>
        <div>
          <p className="text-xs uppercase text-muted-foreground">Allowed Leave</p>
          <p className="text-sm font-medium text-foreground">{summary?.allowedLeaveDays ?? "-"}</p>
        </div>
        <div>
          <p className="text-xs uppercase text-muted-foreground">Actual Working Hours</p>
          <p className="text-sm font-medium text-foreground">{formatHoursAsHhMm(summary?.actualWorkingHours)}</p>
        </div>
        <div>
          <p className="text-xs uppercase text-muted-foreground">Expected Working Hours</p>
          <p className="text-sm font-medium text-foreground">{formatHoursAsHhMm(summary?.expectedWorkingHrs)}</p>
        </div>
        <div>
          <p className="text-xs uppercase text-muted-foreground">Leave Taken</p>
          <p className="text-sm font-medium text-foreground">{summary?.actualLeaveTaken ?? "-"}</p>
        </div>
        <div>
          <p className="text-xs uppercase text-muted-foreground">LOP</p>
          <p className="text-sm font-medium text-foreground">{summary?.lop ?? "-"}</p>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border">
        <table className="w-full min-w-[720px] border-collapse text-sm">
          <thead>
            <tr className="border-b bg-muted/50 text-left text-xs font-medium uppercase text-muted-foreground">
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">Login</th>
              <th className="px-3 py-2">Lunch In</th>
              <th className="px-3 py-2">Lunch Out</th>
              <th className="px-3 py-2">Logout</th>
              <th className="px-3 py-2">Total Hours</th>
              <th className="w-10 px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
              const key = dateKeyForDay(day);
              const edit = getDay(key);
              const collapsed = isNoPunchStatus(edit.statusRefId);
              const currentStatusLabel =
                statusOptions?.find((s: any) => s?.id === edit.statusRefId)?.name || null;
              const totalHours = computeTotalHours(edit);

              const isHighlighted = day === highlightedDay;

              return (
                <tr
                  key={day}
                  ref={isHighlighted ? highlightedRowRef : undefined}
                  className={cn(
                    "border-b last:border-b-0 transition-colors",
                    ROW_STATUS_CLASSES[statusName(edit.statusRefId)] || "hover:bg-muted/30",
                    isHighlighted && "ring-2 ring-inset ring-primary bg-primary/10"
                  )}
                >
                  <td className="whitespace-nowrap px-3 py-2 font-medium text-foreground">
                    {moment(key).format("DD MMM")}
                  </td>
                  {collapsed ? (
                    <td colSpan={5} className="px-3 py-2 text-center font-medium text-muted-foreground">
                      {currentStatusLabel}
                    </td>
                  ) : (
                    <>
                      <td className="px-3 py-2">
                        <Input
                          type="time"
                          className="h-8 text-xs"
                          value={edit.loginTime}
                          onChange={(e) => setDayField(day, "loginTime", e.target.value)}
                          onBlur={() => scheduleAutoSave(day)}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <Input
                          type="time"
                          className="h-8 text-xs"
                          value={edit.lunchInTime}
                          onChange={(e) => setDayField(day, "lunchInTime", e.target.value)}
                          onBlur={() => scheduleAutoSave(day)}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <Input
                          type="time"
                          className="h-8 text-xs"
                          value={edit.lunchOutTime}
                          onChange={(e) => setDayField(day, "lunchOutTime", e.target.value)}
                          onBlur={() => scheduleAutoSave(day)}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <Input
                          type="time"
                          className="h-8 text-xs"
                          value={edit.logoutTime}
                          onChange={(e) => setDayField(day, "logoutTime", e.target.value)}
                          onBlur={() => scheduleAutoSave(day)}
                        />
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-center font-medium text-foreground">
                        {formatHoursAsHhMm(totalHours)}
                      </td>
                    </>
                  )}
                  <td className="px-3 py-2 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {/* {statusOptions?.map((opt: any) => ( */}
                        {statusOptions
                          ?.filter((opt: any) => String(opt?.name || "").toLowerCase() !== "absent")
                          ?.map((opt: any) => (
                            <DropdownMenuItem key={opt?.id} onClick={() => setDayStatusAndSave(day, opt?.id)}>
                              {opt?.name}
                            </DropdownMenuItem>
                          ))}
                        {/* {edit.statusRefId != null && (
                          <DropdownMenuItem onClick={() => setDayStatusAndSave(day, null)}>
                            <X className="h-4 w-4" /> Clear
                          </DropdownMenuItem>
                        )} */}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <LoadingOverlay loading={getEmployeeAttendanceCalendarLoad || markAttendanceLoad} />
    </div>
  );
};

export default EmployeeAttendanceCalendar;
